import type { Locale, Messages } from "../i18n/types";
import { EXTERNAL_LINKS } from "../shared/external-links";
import type {
  BrowserEnvironment,
  PublicRuntimeConfig,
  ScanResponse,
  WebRtcObservation
} from "../shared/contracts";
import { CAYLET_RISK_THRESHOLDS } from "../shared/risk-config";
import { buildConnectionCard, buildEvidenceSections } from "./evidence-view";
import { buildSiteFooter } from "./footer";
import { buildLocaleControl as buildSharedLocaleControl, buildSiteHeader } from "./header";
import { buildIpMapStage } from "./hero-map";
import type { PingResult } from "./ping-detection";

export type ScanPhase = "idle" | "local" | "network" | "complete" | "error";

export interface AppState {
  locale: Locale;
  phase: ScanPhase;
  browser: BrowserEnvironment | null;
  scan: ScanResponse | null;
  webrtc: WebRtcObservation | null;
  ping: PingResult[] | null;
  error: string | null;
  runtimeConfig: PublicRuntimeConfig;
  hasScanned: boolean;
}

export interface AppHandlers {
  onScan: () => void;
  onLocale: (locale: Locale) => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function getAbuseIpDb(scan: ScanResponse | null) {
  if (scan?.abuseipdb) return scan.abuseipdb;
  const reputation = scan?.reputation;
  return reputation?.abuseConfidenceScore !== undefined && reputation.abuseReportCount !== undefined
    ? {
        abuseConfidenceScore: reputation.abuseConfidenceScore,
        abuseReportCount: reputation.abuseReportCount
      }
    : null;
}

function externalLink(label: string, href: string, className = "text-link"): HTMLAnchorElement {
  const link = el("a", className, label);
  link.href = href;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  return link;
}

function buildLocaleControl(state: AppState, copy: Messages, handlers: AppHandlers): HTMLElement {
  const options = [
    { locale: "en", label: copy.nav.languageNames.en },
    { locale: "zh-CN", label: copy.nav.languageNames.zhCN },
    { locale: "zh-TW", label: copy.nav.languageNames.zhTW }
  ] as const;
  return buildSharedLocaleControl(state.locale, copy, options, handlers.onLocale);
}

function sectionHeading(eyebrow: string, title: string, body?: string): HTMLElement {
  const group = el("div", "section-heading");
  group.append(el("p", "eyebrow", eyebrow), el("h2", "section-title", title));
  if (body) group.append(el("p", "section-copy", body));
  return group;
}

function formatFieldValue(value: string): HTMLElement | string {
  const match = value.match(/^\[([^\]]+)\]\s*([\s\S]*)$/);
  if (!match) return value;
  const tag = match[1];
  const rest = match[2];
  let badgeType = "pill-neutral";
  if (/正常|Match|Clear|Verified/i.test(tag)) {
    badgeType = "pill-safe";
  } else if (/警告|警示|Warning|Alert/i.test(tag)) {
    badgeType = "pill-danger";
  } else if (/注意|Caution|Observed/i.test(tag)) {
    badgeType = "pill-warn";
  }
  const span = el("span", "field-status-val");
  const pill = el("span", `field-status-pill ${badgeType}`, `[${tag}]`);
  const text = el("span", "field-status-text", rest ? ` ${rest}` : "");
  span.append(pill, text);
  return span;
}

function field(label: string, value: string, mono = false): HTMLElement {
  const row = el("div", "field");
  const dd = el("dd", mono ? "field-value mono" : "field-value");
  const formatted = formatFieldValue(value);
  if (typeof formatted === "string") {
    dd.textContent = formatted;
  } else {
    dd.append(formatted);
  }
  row.append(el("dt", "field-label", label), dd);
  return row;
}

type FraudProviderInfoKey = keyof Messages["cards"]["fraudCard"]["providerInfo"];

function buildFraudProviderSource(
  providerName: string,
  attribution: string,
  infoKey: FraudProviderInfoKey,
  copy: Messages
): HTMLElement {
  const source = el("span", "fraud-provider-col col-source");
  const nameLine = el("span", "provider-name-line");
  const infoButton = el("button", "provider-info-button", "?");
  const buttonLabel = copy.cards.fraudCard.providerInfoLabel.replace("{provider}", providerName);
  infoButton.type = "button";
  infoButton.title = buttonLabel;
  infoButton.dataset.providerInfo = infoKey;
  infoButton.setAttribute("aria-label", buttonLabel);
  infoButton.setAttribute("aria-haspopup", "dialog");
  infoButton.setAttribute("aria-controls", "provider-info-dialog");
  infoButton.addEventListener("click", () => {
    const dialog = document.querySelector<HTMLDialogElement>("#provider-info-dialog");
    const title = dialog?.querySelector<HTMLElement>("[data-provider-dialog-title]");
    const body = dialog?.querySelector<HTMLElement>("[data-provider-dialog-copy]");
    if (!dialog || !title || !body) return;
    const content = copy.cards.fraudCard.providerInfo[infoKey];
    title.textContent = content.title;
    body.textContent = content.body;
    dialog.addEventListener("close", () => infoButton.focus(), { once: true });
    dialog.showModal();
  });
  nameLine.append(document.createTextNode(providerName), infoButton);
  source.append(nameLine, el("small", "provider-auth", attribution));
  return source;
}

function buildFraudScoreCard(state: AppState, copy: Messages): HTMLElement {
  const card = el("div", "fraud-score-card");

  // Header
  const header = el("div", "fraud-score-header");
  const titleWrap = el("div", "fraud-score-title-wrap");
  titleWrap.append(
    el("h2", "fraud-score-title", copy.evidence.reputation),
    el("span", "fraud-score-eyebrow", copy.cards.reputationSub)
  );
  header.append(titleWrap);

  // Native Scamalytics score; never combine unlike provider metrics.
  const scamalyticsScore = state.scan?.reputation?.score;
  const reputationScore = scamalyticsScore;
  const abuseScore = getAbuseIpDb(state.scan)?.abuseConfidenceScore;

  const scoreDisplay = el("div", "fraud-score-display");
  const scoreNum = el("div", "fraud-score-number", reputationScore !== undefined ? String(reputationScore) : "—");
  const scoreMax = el("span", "fraud-score-max", reputationScore !== undefined ? " / 100" : "");
  scoreDisplay.append(scoreNum, scoreMax);
  header.append(scoreDisplay);
  card.append(header);

  // Risk band label
  if (reputationScore !== undefined) {
    let bandClass = "fraud-band-low";
    let bandText = copy.cards.fraudCard.bandLow;
    if (reputationScore > CAYLET_RISK_THRESHOLDS.mediumMax) {
      bandClass = "fraud-band-high";
      bandText = copy.cards.fraudCard.bandHigh;
    } else if (reputationScore > CAYLET_RISK_THRESHOLDS.lowMax) {
      bandClass = "fraud-band-medium";
      bandText = copy.cards.fraudCard.bandMedium;
    }
    const band = el("div", `fraud-score-band ${bandClass}`, bandText);
    card.append(band);

    // Gauge bar
    const gaugeWrap = el("div", "fraud-gauge-wrap");
    const gaugeBar = el("div", "fraud-gauge-bar");
    const gaugeFill = el("div", `fraud-gauge-fill ${bandClass}`);
    gaugeFill.style.width = `${Math.min(100, reputationScore)}%`;
    gaugeBar.append(gaugeFill);
    gaugeWrap.append(gaugeBar);
    card.append(gaugeWrap);
  }

  // Provider breakdown table
  const table = el("div", "fraud-provider-table");
  const tableHead = el("div", "fraud-provider-row fraud-provider-header");
  tableHead.append(
    el("span", "fraud-provider-col col-source", copy.cards.fraudCard.colSource),
    el("span", "fraud-provider-col col-value", copy.cards.fraudCard.colValue),
    el("span", "fraud-provider-col col-status", copy.cards.fraudCard.colStatus)
  );
  table.append(tableHead);

  const noData = copy.cards.fraudCard.evidenceNoData;

  // Row: Scamalytics
  const scRow = el("div", "fraud-provider-row");
  const scValue = scamalyticsScore !== undefined ? `${scamalyticsScore} / 100` : noData;
  const scStatus = scamalyticsScore !== undefined
    ? (scamalyticsScore <= CAYLET_RISK_THRESHOLDS.lowMax
        ? copy.cards.fraudCard.statusLow
        : scamalyticsScore > CAYLET_RISK_THRESHOLDS.mediumMax
          ? copy.cards.fraudCard.statusHigh
          : copy.cards.fraudCard.statusMedium)
    : noData;
  const scStatusClass = scamalyticsScore !== undefined
    ? (scamalyticsScore <= CAYLET_RISK_THRESHOLDS.lowMax
        ? "status-dot-low"
        : scamalyticsScore > CAYLET_RISK_THRESHOLDS.mediumMax
          ? "status-dot-high"
          : "status-dot-medium")
    : "status-dot-unknown";
  scRow.append(
    buildFraudProviderSource("Scamalytics", copy.cards.fraudCard.authScamalytics, "scamalytics", copy),
    el("span", "fraud-provider-col col-value mono", scValue),
    (() => { const s = el("span", `fraud-provider-col col-status ${scStatusClass}`, scStatus); return s; })()
  );
  table.append(scRow);

  // Row: IP2Location
  const i2lRow = el("div", "fraud-provider-row");
  const i2lProxy = state.scan?.ip2location?.isProxy;
  const i2lValue = i2lProxy !== undefined ? `${copy.cards.proxy}: ${i2lProxy ? copy.common.yes : copy.common.no}` : noData;
  const i2lStatus = i2lProxy !== undefined
    ? (i2lProxy ? copy.cards.fraudCard.proxyYes : copy.cards.fraudCard.proxyNo)
    : noData;
  const i2lStatusClass = i2lProxy === undefined ? "status-dot-unknown" : i2lProxy ? "status-dot-high" : "status-dot-neutral";
  i2lRow.append(
    buildFraudProviderSource("IP2Location", copy.cards.fraudCard.authIp2Location, "ip2location", copy),
    el("span", "fraud-provider-col col-value mono", i2lValue),
    (() => { const s = el("span", `fraud-provider-col col-status ${i2lStatusClass}`, i2lStatus); return s; })()
  );
  table.append(i2lRow);

  // Row: AbuseIPDB
  const abRow = el("div", "fraud-provider-row");
  const abValue = abuseScore !== undefined ? `${abuseScore}%` : noData;
  const abuse = getAbuseIpDb(state.scan);
  const abStatus = abuse
    ? `${copy.cards.abuseReports}: ${abuse.abuseReportCount}${state.scan?.abuseipdb?.lastReportedAt ? " · " + copy.evidence.lastReported + ": " + state.scan.abuseipdb.lastReportedAt : ""}`
    : noData;
  const abStatusClass = abuse ? "status-dot-neutral" : "status-dot-unknown";
  abRow.append(
    buildFraudProviderSource("AbuseIPDB", copy.cards.fraudCard.authAbuseIPDB, "abuseIpDb", copy),
    el("span", "fraud-provider-col col-value mono", abValue),
    (() => { const s = el("span", `fraud-provider-col col-status ${abStatusClass}`, abStatus); return s; })()
  );
  table.append(abRow);

  // Row: IPinfo
  const ipinfoRow = el("div", "fraud-provider-row");
  const ipinfoAsn = state.scan?.ipinfo?.asnNumber;
  const ipinfoOrg = state.scan?.ipinfo?.asnName;
  const ipinfoValue = ipinfoAsn ? `${ipinfoAsn}${ipinfoOrg ? ` · ${ipinfoOrg}` : ""}` : noData;
  const ipinfoStatus = ipinfoAsn ? copy.evidence.identityOnly : noData;
  const ipinfoStatusClass = ipinfoAsn ? "status-dot-neutral" : "status-dot-unknown";
  ipinfoRow.append(
    buildFraudProviderSource("IPinfo.io", copy.cards.fraudCard.authIpinfo, "ipinfo", copy),
    el("span", "fraud-provider-col col-value mono", ipinfoValue),
    (() => { const s = el("span", `fraud-provider-col col-status ${ipinfoStatusClass}`, ipinfoStatus); return s; })()
  );
  table.append(ipinfoRow);

  // Row: locally matched public OSINT botnet C2 blocklist
  const osintRow = el("div", "fraud-provider-row");
  const osintResult = state.scan?.osintBlocklist;
  const osintValue = osintResult
    ? `${osintResult.entriesChecked} ${copy.cards.fraudCard.osintEntries}`
    : noData;
  const osintStatus = !osintResult
    ? noData
    : osintResult.matched
      ? copy.cards.fraudCard.osintMatch
      : copy.cards.fraudCard.osintNoMatch;
  const osintStatusClass = osintResult?.matched
    ? "status-dot-high"
    : osintResult
      ? "status-dot-neutral"
      : "status-dot-unknown";
  osintRow.append(
    buildFraudProviderSource("abuse.ch", copy.cards.fraudCard.authOsint, "abuseCh", copy),
    el("span", "fraud-provider-col col-value mono", osintValue),
    (() => { const s = el("span", `fraud-provider-col col-status ${osintStatusClass}`, osintStatus); return s; })()
  );
  table.append(osintRow);

  card.append(table);

  // Note
  const flagList = el("dl", "param-group-list");
  const rep = state.scan?.reputation;
  for (const [label, value] of [
    [copy.cards.proxy, rep?.proxy], [copy.cards.vpn, rep?.vpn],
    [rep?.torSource === "tor_project" ? "Tor · Tor Project" : "Tor · Scamalytics", rep?.tor],
    [copy.cards.datacenter, rep?.datacenter]
  ] as const) flagList.append(field(label, value === undefined ? copy.common.unavailable : value ? copy.common.yes : copy.common.no));
  card.append(flagList, el("p", "fraud-score-note", copy.evidence.reputationNote));
  return card;
}

function buildResults(state: AppState, copy: Messages, handlers: AppHandlers): HTMLElement {
  const section = el("section", "results-section");
  section.id = "results";
  section.setAttribute("aria-labelledby", "results-title");
  const title = el("h2", "sr-only", copy.scanner.complete);
  title.id = "results-title";
  section.append(title);

  const dashboard = el("article", "dashboard-panel");

  // Borderless Top IP Telemetry & Vector Map Stage
  dashboard.append(buildIpMapStage(state, copy));

  // Secondary Metrics Console Grid (Fraud Score Card + Connection & Edge Card)
  if (state.hasScanned) {
    const topGrid = el("div", "dashboard-top-grid");
    topGrid.append(
      buildFraudScoreCard(state, copy),
      buildConnectionCard(state, copy)
    );
    dashboard.append(topGrid, buildEvidenceSections(state, copy));
  }

  const unavailable = copy.common.unavailable;

  let gpuStatusText = unavailable;
  if (state.browser?.isVirtualGpu === true) {
    gpuStatusText = copy.cards.virtualGpu;
  } else if (
    state.browser?.gpu !== undefined &&
    state.browser.gpu !== "Unavailable" &&
    state.browser.gpu !== "Unknown"
  ) {
    gpuStatusText = copy.cards.gpuUnclassified;
  }
  let webdriverText = unavailable;
  if (state.browser?.webdriver !== undefined) {
    webdriverText = state.browser.webdriver
      ? copy.cards.webdriverDetected
      : copy.cards.webdriverNotExposed;
  }

  // Technical parameters categorized container (Client Device & Browser Environment)
  const unifiedList = el("div", "dashboard-flat-grid");

  // Group 1: Hardware & System Specifications
  const hardwareCard = el("div", "param-group-card");
  const hardwareHeader = el("div", "param-group-header");
  hardwareHeader.innerHTML = `
    <span class="param-group-icon">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
        <line x1="8" y1="21" x2="16" y2="21"></line>
        <line x1="12" y1="17" x2="12" y2="21"></line>
      </svg>
    </span>
    <h3 class="param-group-title">${copy.cards.hardware}</h3>
  `;
  const hardwareList = el("dl", "param-group-list");
  hardwareList.append(
    field(copy.cards.os, state.browser?.os ?? unavailable),
    field(copy.cards.screen, state.browser?.screen ?? unavailable),
    field(copy.cards.cores, state.browser?.cores?.toLocaleString(state.locale) ?? unavailable),
    field(copy.cards.gpu, state.browser?.gpu ?? unavailable, true),
    field(copy.cards.gpuStatus, gpuStatusText)
  );
  hardwareCard.append(hardwareHeader, hardwareList);

  // Group 2: Browser & Client Environment
  const browserCard = el("div", "param-group-card");
  const browserHeader = el("div", "param-group-header");
  browserHeader.innerHTML = `
    <span class="param-group-icon">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"></polygon>
      </svg>
    </span>
    <h3 class="param-group-title">${copy.cards.browser}</h3>
  `;
  const browserList = el("dl", "param-group-list");
  browserList.append(
    field(copy.cards.browserName, state.browser?.browser ?? unavailable),
    field(copy.cards.language, state.browser?.language ?? unavailable),
    field(copy.cards.deviceTimezone, state.browser?.timezone ?? unavailable),
    field(copy.cards.webdriver, webdriverText)
  );
  browserCard.append(browserHeader, browserList);

  unifiedList.append(hardwareCard, browserCard);
  dashboard.append(unifiedList);

  const interpretation = el(
    "p",
    "risk-note",
    copy.evidence.disclaimer
  );
  dashboard.append(interpretation);

  const disclaimer = el("div", "dashboard-disclaimer");
  const disclaimerList = el("ul", "dashboard-disclaimer-list");
  for (const item of copy.interpretation.limits) {
    disclaimerList.append(el("li", "", item));
  }
  disclaimer.append(disclaimerList);
  
  dashboard.append(disclaimer);

  const rescanWrap = el("div", "dashboard-action-bottom");
  const rescanButton = el("button", "primary-button rescan-dash-button", copy.scanner.rescan);
  rescanButton.type = "button";
  rescanButton.disabled = state.phase === "local" || state.phase === "network";
  rescanButton.addEventListener("click", handlers.onScan);
  rescanWrap.append(rescanButton);
  dashboard.append(rescanWrap);

  section.append(dashboard);
  return section;
}

function buildDialog(copy: Messages): HTMLDialogElement {
  const dialog = el("dialog", "info-dialog");
  dialog.id = "info-dialog";
  const panel = el("div", "dialog-panel");
  panel.append(el("p", "dialog-copy", ""));
  panel.lastElementChild?.setAttribute("data-dialog-copy", "");
  const close = el("button", "secondary-button", copy.common.close);
  close.type = "button";
  close.addEventListener("click", () => dialog.close());
  panel.append(close);
  dialog.append(panel);
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
  return dialog;
}

function buildProviderInfoDialog(copy: Messages): HTMLDialogElement {
  const initial = copy.cards.fraudCard.providerInfo.scamalytics;
  const dialog = el("dialog", "info-dialog provider-info-dialog");
  dialog.id = "provider-info-dialog";
  dialog.setAttribute("aria-labelledby", "provider-info-dialog-title");
  const panel = el("div", "dialog-panel provider-info-dialog-panel");
  const title = el("h2", "provider-info-dialog-title", initial.title);
  title.id = "provider-info-dialog-title";
  title.setAttribute("data-provider-dialog-title", "");
  const body = el("p", "dialog-copy provider-info-dialog-copy", initial.body);
  body.setAttribute("data-provider-dialog-copy", "");
  const close = el("button", "secondary-button provider-info-close", copy.common.close);
  close.type = "button";
  close.addEventListener("click", () => dialog.close());
  panel.append(title, body, close);
  dialog.append(panel);
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
  return dialog;
}

function buildPreloader(state: AppState): HTMLElement | null {
  if (state.phase === "complete" || state.phase === "error") {
    return null;
  }

  const overlay = el("div", "preloader-overlay");

  // Prevent background scrolling while preloader is active
  document.body.style.overflow = "hidden";

  const content = el("div", "preloader-content");
  
  const text = el("span", "preloader-text", "CAYLET IP CHECKER");
  content.append(text);

  const barContainer = el("div", "preloader-bar-container");
  const bar = el("div", "preloader-bar");
  barContainer.append(bar);
  content.append(barContainer);

  overlay.append(content);
  return overlay;
}

export function renderApp(
  root: HTMLElement,
  state: AppState,
  copy: Messages,
  handlers: AppHandlers
): void {
  // Reset body overflow in case it was locked by the preloader
  document.body.style.overflow = "";

  const fragment = document.createDocumentFragment();

  const preloader = buildPreloader(state);
  if (preloader) {
    fragment.append(preloader);
  }

  const skip = el("a", "skip-link", copy.a11y.skip);
  skip.href = "#main-content";
  fragment.append(skip);

  const header = buildSiteHeader(copy, state.locale, buildLocaleControl(state, copy, handlers));
  fragment.append(header);


  const main = el("main");
  main.id = "main-content";
  main.append(buildResults(state, copy, handlers));

  // Conclusion (Founder Message Style)
  const conclusionSection = el("div", "founder-message-section");
  const bgGlow = el("div", "founder-message-bg-glow");
  const container = el("div", "founder-message-container");
  const grid = el("div", "founder-message-grid");

  // Left Side: Avatar
  const avatarWrap = el("div", "founder-avatar-wrap");
  const avatarGlow = el("div", "founder-avatar-glow");
  const avatarImgWrap = el("div", "founder-avatar");
  const avatarImg = el("img") as HTMLImageElement;
  avatarImg.src = "/images/aiden-profile.png";
  avatarImg.alt = "Aiden";
  avatarImg.draggable = false;
  avatarImgWrap.append(avatarImg);
  avatarWrap.append(avatarGlow, avatarImgWrap);

  // Right Side: Quote
  const quoteWrap = el("div", "founder-quote-wrap");
  const quoteMark = el("div", "founder-quote-mark", "“");
  const quoteBody = el("blockquote", "founder-quote-body", copy.interpretation.conclusion);
  
  const signature = el("div", "founder-signature");
  const cite = el("cite");
  const name = el("span", "founder-name", "AIDEN");
  const titleText = el("span", "founder-title", "FOUNDER OF CAYLET LLC");
  cite.append(name, titleText);
  signature.append(cite);

  quoteWrap.append(quoteMark, quoteBody, signature);
  grid.append(avatarWrap, quoteWrap);
  container.append(grid);
  conclusionSection.append(bgGlow, container);

  main.append(conclusionSection);

function buildWorldNetworkMapSVG(): HTMLElement {
  const container = el("div", "esim-network-overlay");
  const svgNS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(svgNS, "svg");
  svg.setAttribute("viewBox", "0 0 1200 600");
  svg.setAttribute("fill", "none");
  svg.setAttribute("class", "esim-network-svg");

  const path1 = document.createElementNS(svgNS, "path");
  path1.setAttribute("d", "M 150,150 L 300,100 L 450,200 L 300,350 L 150,150 Z");
  path1.setAttribute("stroke", "#bf953f");
  path1.setAttribute("stroke-width", "1.5");
  path1.setAttribute("stroke-dasharray", "4 4");

  const path2 = document.createElementNS(svgNS, "path");
  path2.setAttribute("d", "M 450,200 L 600,120 L 750,220 L 600,380 L 450,200 Z");
  path2.setAttribute("stroke", "#bf953f");
  path2.setAttribute("stroke-width", "1.5");
  path2.setAttribute("stroke-dasharray", "4 4");

  const path3 = document.createElementNS(svgNS, "path");
  path3.setAttribute("d", "M 750,220 L 950,150 L 1050,300 L 850,420 L 750,220 Z");
  path3.setAttribute("stroke", "#bf953f");
  path3.setAttribute("stroke-width", "1.5");
  path3.setAttribute("stroke-dasharray", "4 4");

  const path4 = document.createElementNS(svgNS, "path");
  path4.setAttribute("d", "M 300,350 L 600,380 L 850,420 L 550,520 L 300,350 Z");
  path4.setAttribute("stroke", "#bf953f");
  path4.setAttribute("stroke-width", "1.5");
  path4.setAttribute("stroke-dasharray", "4 4");

  const line1 = document.createElementNS(svgNS, "line");
  line1.setAttribute("x1", "300"); line1.setAttribute("y1", "100"); line1.setAttribute("x2", "600"); line1.setAttribute("y2", "120");
  line1.setAttribute("stroke", "#bf953f"); line1.setAttribute("stroke-width", "1");

  const line2 = document.createElementNS(svgNS, "line");
  line2.setAttribute("x1", "600"); line2.setAttribute("y1", "380"); line2.setAttribute("x2", "850"); line2.setAttribute("y2", "420");
  line2.setAttribute("stroke", "#bf953f"); line2.setAttribute("stroke-width", "1");

  const line3 = document.createElementNS(svgNS, "line");
  line3.setAttribute("x1", "450"); line3.setAttribute("y1", "200"); line3.setAttribute("x2", "550"); line3.setAttribute("y2", "520");
  line3.setAttribute("stroke", "#bf953f"); line3.setAttribute("stroke-width", "1");

  const nodes = [
    { cx: 150, cy: 150, r: 6, fill: "#bf953f", pulse: false },
    { cx: 300, cy: 100, r: 4, fill: "#bf953f", pulse: false },
    { cx: 450, cy: 200, r: 8, fill: "#fcf6ba", pulse: true },
    { cx: 300, cy: 350, r: 5, fill: "#bf953f", pulse: false },
    { cx: 600, cy: 120, r: 6, fill: "#bf953f", pulse: false },
    { cx: 750, cy: 220, r: 8, fill: "#fcf6ba", pulse: true },
    { cx: 600, cy: 380, r: 5, fill: "#bf953f", pulse: false },
    { cx: 950, cy: 150, r: 4, fill: "#bf953f", pulse: false },
    { cx: 1050, cy: 300, r: 6, fill: "#bf953f", pulse: false },
    { cx: 850, cy: 420, r: 8, fill: "#fcf6ba", pulse: true },
    { cx: 550, cy: 520, r: 6, fill: "#bf953f", pulse: false }
  ];

  svg.append(path1, path2, path3, path4, line1, line2, line3);

  nodes.forEach((n) => {
    const circle = document.createElementNS(svgNS, "circle");
    circle.setAttribute("cx", String(n.cx));
    circle.setAttribute("cy", String(n.cy));
    circle.setAttribute("r", String(n.r));
    circle.setAttribute("fill", n.fill);
    if (n.pulse) circle.setAttribute("class", "esim-node-pulse");
    svg.append(circle);
  });

  container.append(svg);
  return container;
}

  const esim = el("section", "esim-cta");

  const bgVideo = document.createElement("video");
  bgVideo.className = "esim-bg-video";
  bgVideo.src = "https://videos.pexels.com/video-files/3125396/3125396-hd_1920_1080_25fps.mp4";
  bgVideo.autoplay = true;
  bgVideo.loop = true;
  bgVideo.muted = true;
  bgVideo.defaultMuted = true;
  bgVideo.playsInline = true;
  bgVideo.setAttribute("autoplay", "");
  bgVideo.setAttribute("loop", "");
  bgVideo.setAttribute("muted", "");
  bgVideo.setAttribute("playsinline", "");
  bgVideo.setAttribute("aria-hidden", "true");

  const source = document.createElement("source");
  source.src = "https://videos.pexels.com/video-files/3125396/3125396-hd_1920_1080_25fps.mp4";
  source.type = "video/mp4";
  bgVideo.append(source);

  const overlay = el("div", "esim-bg-overlay");
  const networkOverlay = buildWorldNetworkMapSVG();

  const eyebrowTag = el("div", "esim-badge-tag");
  eyebrowTag.append(
    el("span", "esim-badge-dot"),
    el("span", "esim-badge-text", copy.esim.eyebrow)
  );

  const esimCopy = el("div", "cta-copy");
  esimCopy.append(
    eyebrowTag,
    el("h2", "section-title", copy.esim.title),
    el("p", "section-copy", copy.esim.body)
  );

  esim.append(bgVideo, overlay, networkOverlay, esimCopy, externalLink(copy.esim.cta, EXTERNAL_LINKS.esim, "primary-link"));
  main.append(esim);

  // Trigger media load & playback after DOM attachment
  setTimeout(() => {
    try {
      bgVideo.load();
      const playPromise = bgVideo.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    } catch {
      // Ignored in non-browser environments
    }
  }, 0);

  const interpretation = el("section", "interpretation-section");
  // Section heading
  interpretation.append(
    sectionHeading(copy.interpretation.eyebrow, copy.interpretation.title, copy.interpretation.body)
  );

  // Core principle callout
  const principle = el("div", "interpretation-principle", copy.interpretation.corePrinciple);
  interpretation.append(principle);

  // Signal weight tiers (Glowing Badge Grid)
  const tiersWrap = el("div", "signal-tiers-wrap");
  const tiersTitle = el("h3", "interp-sub-title", copy.interpretation.signalWeightsTitle);
  tiersWrap.append(tiersTitle);
  const tiersGrid = el("div", "signal-badges-grid");
  
  copy.interpretation.signalTiers.forEach((tier, idx) => {
    const groupWrap = el("div", "signal-badge-group");
    groupWrap.append(el("div", `signal-badge-label tier-${idx + 1}-label`, tier.label));
    const badgeList = el("div", "signal-badge-list");
    for (const sig of tier.signals) {
      badgeList.append(el("span", `signal-badge badge-tier-${idx + 1}`, sig));
    }
    groupWrap.append(badgeList);
    tiersGrid.append(groupWrap);
  });
  tiersWrap.append(tiersGrid);
  interpretation.append(tiersWrap);

  // Scenario range bar chart
  const scenarioWrap = el("div", "interp-scenario-wrap");
  scenarioWrap.append(el("h3", "interp-sub-title", copy.interpretation.scenarioTitle));
  scenarioWrap.append(el("p", "interp-scenario-note", copy.interpretation.scenarioNote));
  
  const scenarioList = el("div", "scenario-list");
  for (const row of copy.interpretation.scenarios) {
    const item = el("div", "scenario-item");
    const header = el("div", "scenario-header");
    
    const riskWrapper = el("span", "scenario-risk-wrapper");
    // @ts-ignore
    const riskLabel = el("span", "scenario-risk-label", (copy.interpretation.riskFactor || "Risk Score") + ": ");
    const riskValue = el("span", "scenario-risk-value", row.risk);
    riskWrapper.append(riskLabel, riskValue);
    
    header.append(el("span", "scenario-desc", row.scenario));
    header.append(riskWrapper);
    
    const track = el("div", "scenario-bar-track");
    const fill = el("div", "scenario-bar-fill");
    
    // Parse min/max from string (e.g. "5-15", "5–15")
    const matches = row.risk.match(/\d+/g);
    if (matches && matches.length >= 2) {
      const max = Math.min(100, Math.max(0, Number(matches[1])));
      fill.style.left = `0%`;
      fill.style.width = `${max}%`;
      if (max <= 30) {
        fill.classList.add("risk-bar-low");
        item.classList.add("tier-low");
      } else if (max <= 65) {
        fill.classList.add("risk-bar-medium");
        item.classList.add("tier-medium");
      } else {
        fill.classList.add("risk-bar-high");
        item.classList.add("tier-high");
      }
    }
    
    track.append(fill);
    item.append(header, track);
    scenarioList.append(item);
  }
  
  scenarioWrap.append(scenarioList);
  interpretation.append(scenarioWrap);


  main.append(interpretation);

  fragment.append(main);

  const { footer, modals } = buildSiteFooter(copy);
  fragment.append(
    footer,
    ...modals,
    buildProviderInfoDialog(copy),
    buildDialog(copy)
  );

  root.replaceChildren(fragment);
}
